import { useEffect, useMemo, useState } from 'react';
import { ValueType } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/mapping.pb';
import { uniq } from 'lodash-es';
import { ScheduleQueries } from '@frontend/api-schedule';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { getFullName } from '@frontend/name';
import { theme } from '@frontend/theme';
import { Chip, Heading, SpinningLoader, styles, Text } from '@frontend/design-system';
import { DUMMY_CONTACT_DETAILS, PracticeVertical } from '../../components/onboarding-flow-components/constants';
import { ProviderSelectionStep } from '../../components/onboarding-flow-components/provider-selection-step';
import { useStackedCardWizardContext } from '../../components/onboarding-flow-components/stacked-card-wizard/stacked-card-wizard-context';
import { StackedCardWizardStep } from '../../components/onboarding-flow-components/stacked-card-wizard/stacked-card-wizard-step-compound';
import { useStepIndexContext } from '../../components/onboarding-flow-components/stacked-card-wizard/step-index-context';
import { useStepData } from '../../components/onboarding-flow-components/stacked-card-wizard/use-step-data';
import { useOnboardingFlowInfo } from '../../hooks/onboarding-flow-hooks/use-onboarding-flow-info';
import { useGetCustomContactSourceId } from '../../hooks/scheduler-v3/use-get-custom-contact-source-id';
import { useSchedulingLocationInfo } from '../../hooks/use-scheduling-location-info';
import { trackOnboardingFlow } from '../../utils/track-onboarding-flow';
import { AppointmentTypeStepData, LocationStepData, OnboardingStep, ProviderStepData, VerticalStepData } from './types';

export const ProviderStep = () => {
  const { currentStep } = useStackedCardWizardContext();
  const stepIndex = useStepIndexContext();

  // Do not render this step content until active to avoid fetching
  // providers until location is selected in prev steps
  if (currentStep !== stepIndex) return null;

  return <ProviderSelection />;
};

const ProviderSelection = () => {
  const { t } = useTranslation('schedule-onboarding');
  const { parentLocationId, isMultiLocation } = useSchedulingLocationInfo();
  const { customContactSourceId } = useGetCustomContactSourceId();
  const { onboardingFlowInfo, setOnboardingFlowInfo } = useOnboardingFlowInfo();
  const { mergeData } = useStepData<ProviderStepData>(OnboardingStep.Provider);
  const { getAllData } = useStackedCardWizardContext();
  const stepperData = getAllData();
  const vertical = stepperData[OnboardingStep.Vertical] as VerticalStepData;
  const stepperLocation = stepperData[OnboardingStep.Location] as LocationStepData | undefined;
  const locationId = stepperLocation?.locationId || onboardingFlowInfo.locationId || '';
  const appointmentTypeData = stepperData[OnboardingStep.AppointmentType] as AppointmentTypeStepData;

  const [selectedProviderIds, setSelectedProviderIds] = useState<string[]>([]);
  const [name, setName] = useState({ firstName: '', lastName: '' });

  // Fetch providers
  const providersQuery = SchedulerV3.Queries.useListProvidersQuery({
    request: { locationId, showHidden: true },
    // By setting enabled to false, we can opt out of automatic refetching
    // This will make sure newly created provider does not display directly on checkbox listing
    options: { enabled: false },
  });
  useEffect(() => {
    if (!!locationId) providersQuery.refetch();
  }, [locationId]);
  const fetchedProviders = providersQuery.data?.providers || [];

  // Fetch Mappings
  const mappingsQuery = SchedulerV3.Queries.useListMappingsQuery({
    request: { appointmentTypeId: appointmentTypeData.appointmentTypeId, locationIds: [locationId] },
    options: { enabled: !!locationId, staleTime: 0 },
  });
  const fetchedMappings = mappingsQuery.data?.mappings || [];

  const providerOptions = useMemo(() => {
    return fetchedProviders
      .filter((provider) => !provider.hidden)
      .map((provider) => ({
        id: provider.id,
        firstName: provider.firstName || '',
        lastName: provider.lastName || '',
        url: provider.profile?.picUrl || '',
      }));
  }, [fetchedProviders]);

  // Mutations
  const createProviderMutation = SchedulerV3.Mutations.useCreateProviderMutation();
  const createMappingMutation = SchedulerV3.Mutations.useCreateMappingsMutation();
  const deleteMappingMutation = SchedulerV3.Mutations.useDeleteMappingsMutation();
  const addContactMutation = ScheduleQueries.useMutateAddCustomContact(locationId);

  const onNextClick = async () => {
    if (selectedProviderIds || fetchedProviders.length === 0) {
      let providerId = selectedProviderIds[0];
      let contactId = onboardingFlowInfo.contactId;
      let contactDetails;

      // Create new provider
      if (
        (fetchedProviders.length === 0 || selectedProviderIds.includes('add-provider')) &&
        name.firstName &&
        name.lastName
      ) {
        const res = await createProviderMutation.mutateAsync({
          locationId,
          sourceTenantId: locationId,
          firstName: name.firstName,
          lastName: name.lastName,
          displayName: getFullName(name),
          profile: { act: true },
          hidden: false,
          // for non-integrated location, we need to set the sourceId as customContactSourceId,
          // for integrated location, BE set the sourceId as the integration's SourceID automatically and on FE we were not allowing create provider for integrated location
          sourceId: customContactSourceId,
          parentLocationId: isMultiLocation ? parentLocationId : undefined,
        });
        providerId = res?.provider?.id;
      }

      // Map provider to appointment type
      try {
        const existingMappingIds = fetchedMappings.map((mapping) => mapping.id);
        const selectedProviders = uniq([...selectedProviderIds, providerId]).filter(
          (value) => value !== 'add-provider'
        );

        if (existingMappingIds.length > 0) {
          await deleteMappingMutation.mutateAsync({
            mappingIds: existingMappingIds,
          });
        }

        await createMappingMutation.mutateAsync({
          locationId,
          values: selectedProviders,
          valueType: ValueType.VT_PROVIDER,
          appointmentTypeId: appointmentTypeData.appointmentTypeId,
        });
      } catch (err) {
        console.error(err);
      }

      // Create dummy patient
      if (!contactId) {
        contactDetails = vertical?.practiceVertical
          ? DUMMY_CONTACT_DETAILS[vertical?.practiceVertical as PracticeVertical]
          : undefined;
        const res = await addContactMutation.mutateAsync({
          first_name: contactDetails?.firstName ?? '',
          last_name: contactDetails?.lastName ?? '',
          gender: contactDetails?.gender ?? '',
          birthdate: contactDetails?.birthdate ?? '',
        });
        contactId = res.data?.person_id;
      }

      const { firstName, lastName } = name;
      mergeData({ selectedProviderIds: [...selectedProviderIds, providerId], firstName, lastName, contactDetails });

      // Set user preference
      if (providerId !== onboardingFlowInfo.providerId || contactId !== onboardingFlowInfo.contactId) {
        setOnboardingFlowInfo({ providerId, contactId });
      }
    }
    trackOnboardingFlow('wizardProviderStepCompleted');
  };

  const isValid = useMemo(() => {
    if (
      (fetchedProviders.length === 0 || selectedProviderIds.includes('add-provider')) &&
      !(name.firstName && name.lastName)
    ) {
      return false;
    }
    if (selectedProviderIds.length === 0 && fetchedProviders.length > 0) return false;
    return true;
  }, [selectedProviderIds, fetchedProviders.length, name]);

  return (
    <StackedCardWizardStep stepId={OnboardingStep.Provider} canGoNext={isValid} onNext={onNextClick}>
      <StackedCardWizardStep.Header>
        <Heading level={2}>{t('Who will be providing this service?')}</Heading>
        <div css={{ display: 'flex', gap: theme.spacing(1) }}>
          <Text weight='regular'>{t("Patients like to know who they'll be seeing for")}</Text>
          <Chip variant='default'>{appointmentTypeData.appointmentName}</Chip>
        </div>
      </StackedCardWizardStep.Header>

      <StackedCardWizardStep.Content>
        {providersQuery.isLoading ? (
          <div css={styles.flexCenter}>
            <SpinningLoader size='medium' />
          </div>
        ) : (
          <div css={{ maxHeight: 260, overflow: 'auto', paddingTop: theme.spacing(1) }}>
            <ProviderSelectionStep
              providerList={providerOptions}
              selectedProviderIds={selectedProviderIds}
              firstName={name.firstName}
              lastName={name.lastName}
              setFirstName={(value) => setName((prev) => ({ ...prev, firstName: value }))}
              setLastName={(value) => setName((prev) => ({ ...prev, lastName: value }))}
              setSelectedProviders={(providerIds) => setSelectedProviderIds(providerIds)}
            />
          </div>
        )}
      </StackedCardWizardStep.Content>

      <StackedCardWizardStep.Footer />
    </StackedCardWizardStep>
  );
};
